/* ============================================================
   src/shared/format.ts
   ----------------------------------------------------------------
   Small, framework-agnostic formatters shared by desktop and mobile.
   ============================================================ */

/** Customer phone auto-format → "(516) 302-4510".
 *
 *  `prev` is the field's previous value; it lets a backspace over a
 *  re-added formatting char remove a digit instead of stalling. Pass
 *  "" when there is no previous value (e.g. importing a fresh number). */
export function formatCustomerPhone(raw: string, prev: string): string {
  const digits = (raw || "").replace(/\D/g, "");
  // Don't mangle long/unusual input — keep what the user typed.
  if (digits.length > 10) return raw;

  const fmt = (d: string): string => {
    const len = d.length;
    if (len === 0) return "";
    if (len < 3) return `(${d}`;
    if (len === 3) return `(${d})`;
    if (len < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  let d = digits;
  let out = fmt(d);
  // Backspace over a re-added formatting char → remove a digit instead.
  if (prev && raw.length < prev.length && out === prev && d.length > 0) {
    d = d.slice(0, -1);
    out = fmt(d);
  }
  return out;
}

/* ============================================================
   Proposal line items — horizontal skirting supersedes vertical
   ============================================================ */

/** One assembled proposal line-item row, as built by ProposalPage and
 *  MobileProposalPage. Only the fields this filter reads are required. */
type SkirtingFilterRow = {
  key: string;
  typeText?: string;
  description?: string;
};

/** Horizontal skirting is detected by the "horiz" stem so it matches the
 *  DB's "Horiztonal" misspelling as well as the correct spelling. Same
 *  test ContractPage and visualLibrary already use, kept identical on
 *  purpose so the scope narrative, the visual card and the line-item
 *  table all agree about which skirting was chosen. */
function isHorizontalSkirtingText(...parts: Array<string | null | undefined>) {
  return parts.some((p) => {
    const s = (p || "").toString().toLowerCase();
    return s.includes("horiz");
  });
}

/** DISPLAY-ONLY filter: when horizontal skirting is present as an +Add Item,
 *  drop the vertical/standard skirting base row from the rendered table.
 *  Horizontal replaces vertical — a proposal must never print both.
 *
 *  Pricing is untouched: this runs on the already-assembled display rows,
 *  and every subtotal/total is computed upstream from the estimate, so the
 *  vertical skirting cost stays in the total exactly as before.
 *
 *  Left alone deliberately:
 *   - vertical selected with NO horizontal add-item → row still prints;
 *   - the selected skirting is ITSELF horizontal → nothing to supersede;
 *   - the skirting category is Lattice → a genuinely different product,
 *     not the vertical-vs-horizontal pair (mirrors ContractPage).
 *
 *  `skirtingCategory` is the estimate's stored category ("Skirting" |
 *  "Lattice"); `skirtingType` is the selected item's name. */
export function filterSupersededSkirtingRows<T extends SkirtingFilterRow>(
  rows: T[],
  opts: {
    skirtingCategory?: string | null;
    skirtingType?: string | null;
  }
): T[] {
  const list = rows || [];

  // Lattice is its own product — never superseded by horizontal skirting.
  if ((opts.skirtingCategory || "").trim().toLowerCase() === "lattice") {
    return list;
  }

  // The selected skirting is already horizontal → it IS the horizontal row.
  if (isHorizontalSkirtingText(opts.skirtingType)) return list;

  // Only a row that actually SURVIVED assembly counts. Checking the rendered
  // rows (not the raw add-item list) means a horizontal add-on that was
  // zero-cost, optional, or otherwise dropped can't hide the vertical row
  // and leave the proposal with no skirting line at all.
  const hasHorizontalRow = list.some(
    (r) =>
      r.key !== "skirting" &&
      isHorizontalSkirtingText(r.typeText, r.description)
  );
  if (!hasHorizontalRow) return list;

  return list.filter((r) => r.key !== "skirting");
}

/**
 * US state as it must PRINT: uppercased, whitespace trimmed.
 *
 * The estimator's state field is free text, so "ny" typed in a hurry reached
 * the proposal header as "Fort Solagna, ny". Output-side only — what is typed
 * and what is stored are untouched, so this cannot rewrite anyone's data; it
 * just means the document never depends on how it was typed.
 */
export function formatStateCode(raw: string | null | undefined): string {
  return String(raw || "").trim().toUpperCase();
}
